// i18next setup — operator console mirror of apps/customer-web/src/i18n.
//
// Pazar dili tr-TR: konsolu kullanan ekip Türkçe çalışıyor. Eskiden burada
// hiç ezme yoktu, dolayısıyla SDK kaynağı en-US'e düşüyordu ve konsol
// ürünün TEK İngilizce açılan yüzeyiydi. Spec 60 statüter bir dil şartı
// koymuyor (KVKK/VERBİS metinleri customer-web'e ait), yani bu bir uyum
// kısıtı değil tercih — ve tercih artık seçiciyle kullanıcıda.
//
// Operatör realm'i token'da GÜVENİLİR bir dil sinyali taşımıyordu:
// operators realm'inde Keycloak i18n kapalıydı ve supportedLocales ['en']
// idi. Realm artık tr'yi de destekliyor ama sadece claim'e yaslanmak bu
// yüzden yeterli değil — saklanan açık seçim asıl mekanizma.
//
// SUPPORTED_LOCALES + DEFAULT_LOCALE come from @novemai/i18n-sdk so
// adding a locale (e.g., de-DE) requires ONE registration in the SDK
// and every consumer auto-picks it up (ADR-006).
//
// Interpolation stays at {{var}} for now; Phase 4b switches to {var}
// + the canonical ICU MF2 formatter from i18n-sdk's format() (plan
// madde 9.4). Migration is mechanical when the time comes.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import {
  DEFAULT_LOCALE,
  readCachedResolvedLocale,
  readStoredLocale,
  resolveAppLocale,
  SUPPORTED_LOCALES,
  type LocaleCode,
} from "@novemai/i18n-sdk";

import enUS from "./locales/en-US.json";
import trTR from "./locales/tr-TR.json";

import { localeStorage } from "./locale-storage.ts";

export { DEFAULT_LOCALE, SUPPORTED_LOCALES };
export type { LocaleCode };

export const OPERATOR_APP_DEFAULT_LOCALE: LocaleCode = "tr-TR";

/**
 * Boot dili. /ops/v1/me henüz gelmedi (oturum çözülmemiş olabilir); onu
 * useApplyResolvedLocale sonradan uygular — bu yüzden `userPref` yuvasına
 * claim'in bir önceki koşudan kalan ÖNBELLEĞİ verilir, yoksa her yükleme
 * tarayıcı diline boyayıp claim gelince üstüne yazar (görünen kusur: ekran
 * bir an İngilizce açılıp Türkçe'ye dönüyor).
 */
function detectInitialLocale(): LocaleCode {
  const stored = readStoredLocale(localeStorage);
  const cachedClaim = readCachedResolvedLocale(localeStorage);
  if (typeof navigator === "undefined") {
    return stored ?? cachedClaim ?? OPERATOR_APP_DEFAULT_LOCALE;
  }
  const browserPreferences =
    navigator.languages !== undefined && navigator.languages.length > 0
      ? Array.from(navigator.languages)
      : [navigator.language ?? ""];
  return resolveAppLocale({
    stored,
    userPref: cachedClaim,
    browserPreferences,
    appDefault: OPERATOR_APP_DEFAULT_LOCALE,
  });
}

export async function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return i18n;
  await i18n.use(initReactI18next).init({
    lng: detectInitialLocale(),
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    defaultNS: "common",
    ns: ["common"],
    resources: {
      "en-US": { common: enUS },
      "tr-TR": { common: trTR },
    },
    interpolation: {
      // React already escapes — i18next double-escape causes mojibake.
      escapeValue: false,
    },
    returnEmptyString: false,
    returnNull: false,
  });
  return i18n;
}

export { i18n };
export default i18n;
